import {
  useCreateWorkSession,
  useUpdateWorkSession,
} from "@/hooks/useWorkSessions";
import type { WorkSession } from "@/types/workSession.type";
import { showSuccessToast } from "../ui/toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Button } from "../ui/button";
import { Loader2, Calendar as CalendarIcon, Check } from "lucide-react";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar as CalendarComponent } from "../ui/calendar";
import { cn } from "@/lib/utils";
import { useUsers } from "@/hooks/useUser";

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

const workSessionSchema = z
  .object({
    startDate: z.date({
      message: "La date de début est requise",
    }),
    startTime: z.string().min(1, "L'heure de début est requise"),
    endDate: z.date().nullable().optional(),
    endTime: z.string().optional(),
    employeeId: z.string().optional(),
  })
  .refine(
    (data) => {
      // Si une date de fin est sélectionnée, l'heure de fin doit être fournie
      if (data.endDate && !data.endTime) {
        return false;
      }
      return true;
    },
    {
      message: "L'heure de fin est requise si une date de fin est sélectionnée",
      path: ["endTime"], // L'erreur sera affichée sur le champ endTime
    }
  );

type WorkSessionFormValue = z.infer<typeof workSessionSchema>;

interface WorSessionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /** Obligatoire en création (sauf sélection dans le formulaire). En édition, pris sur la session. */
  employeeId?: string;
  date?: Date;
  companyId?: string;
  /** Si défini, le dialogue est en mode modification. */
  sessionToEdit?: WorkSession | null;
  markAsReviewedOnSave?: boolean;
}

const padTimePart = (n: number) => String(n).padStart(2, "0");

const sessionToFormDefaults = (
  session: WorkSession
): WorkSessionFormValue => {
  const s = new Date(session.startTime);
  const startDate = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const startTimeStr = `${padTimePart(s.getHours())}:${padTimePart(s.getMinutes())}`;
  let endDate: Date | null = null;
  let endTimeStr = "";
  if (session.endTime) {
    const e = new Date(session.endTime);
    endDate = new Date(e.getFullYear(), e.getMonth(), e.getDate());
    endTimeStr = `${padTimePart(e.getHours())}:${padTimePart(e.getMinutes())}`;
  }
  return {
    startDate,
    startTime: startTimeStr,
    endDate,
    endTime: endTimeStr,
    employeeId: session.employeeId,
  };
};

const WorSessionFormDialog = ({
  open,
  onOpenChange,
  onSuccess,
  employeeId,
  date,
  sessionToEdit = null,
  markAsReviewedOnSave = false,
}: WorSessionFormDialogProps) => {
  const isEdit = !!sessionToEdit;
  const { mutate: createWorkSessionMutation, isPending: isCreating } =
    useCreateWorkSession();
  const { mutate: updateWorkSessionMutation, isPending: isUpdating } =
    useUpdateWorkSession();
  const isPending = isCreating || isUpdating;

  // États pour gérer l'ouverture des popovers
  const [startDatePopoverOpen, setStartDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const { data: usersData, isFetching: isFetchingUsers } = useUsers({
    limit: 100,
    search: employeeSearch,
    isActive: true,
  });
  const users = usersData?.users || [];
  const [employeePopoverOpen, setEmployeePopoverOpen] = useState(false);

  const defaultValue = useMemo<WorkSessionFormValue>(
    () => ({
      startDate: date || new Date(),
      startTime: "08:00",
      endDate: null,
      endTime: "",
    }),
    [date]
  );

  const form = useForm<WorkSessionFormValue>({
    resolver: zodResolver(workSessionSchema),
    defaultValues: defaultValue,
  });

  const { handleSubmit, reset, watch, formState, control } = form;

  // Observer endDate et endTime pour valider le formulaire
  const endDate = watch("endDate");
  const endTime = watch("endTime");

  // Vérifier si le formulaire est valide
  // Le bouton sera désactivé si :
  // - Le formulaire n'est pas valide (validation Zod)
  // - OU si endDate est fourni mais endTime est vide
  const isFormValid =
    formState.isValid &&
    (!endDate || (endDate && endTime && endTime.trim() !== ""));

  // Réinitialiser le formulaire lorsque le dialog s'ouvre
  useEffect(() => {
    if (!open) return;
    if (sessionToEdit) {
      reset(sessionToFormDefaults(sessionToEdit));
    } else {
      reset({
        startDate: date || new Date(),
        startTime: "08:00",
        endDate: null,
        endTime: "",
        employeeId: employeeId || undefined,
      });
    }
    setEmployeeSearch("");
  }, [open, reset, date, employeeId, sessionToEdit]);

  const onSubmit = (data: WorkSessionFormValue) => {
    // Combiner date et heure pour startTime
    const startDateTime = new Date(data.startDate);
    const [startHours, startMinutes] = data.startTime.split(":");
    startDateTime.setHours(
      parseInt(startHours, 10),
      parseInt(startMinutes, 10),
      0,
      0
    );
    const startTimeISO = startDateTime.toISOString();

    // Combiner date et heure pour endTime (si fourni)
    let endTimeISO: string | null = null;
    if (data.endDate && data.endTime) {
      const endDateTime = new Date(data.endDate);
      const [endHours, endMinutes] = data.endTime.split(":");
      endDateTime.setHours(
        parseInt(endHours, 10),
        parseInt(endMinutes, 10),
        0,
        0
      );
      endTimeISO = endDateTime.toISOString();
    }

    if (sessionToEdit) {
      updateWorkSessionMutation(
        {
          sessionId: sessionToEdit.id,
          data: {
            startTime: startTimeISO,
            endTime: endTimeISO,
            isReviewed: markAsReviewedOnSave ? true : undefined,
          },
        },
        {
          onSuccess: () => {
            showSuccessToast(markAsReviewedOnSave ? "Session marquée comme traitée avec succès" : "Session modifiée avec succès");
            onSuccess?.();
            onOpenChange(false);
            reset();
          },
        }
      );
      return;
    }

    createWorkSessionMutation(
      {
        employeeId: employeeId || data.employeeId || "",
        startTime: startTimeISO,
        endTime: endTimeISO,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onOpenChange(false);
          reset();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-1rem)] sm:max-w-[500px] md:max-w-[600px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {isEdit ? "Modifier la session de travail" : "Créer une session de travail"}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isEdit
              ? "Ajustez les horaires de cette session."
              : "Ajoutez une session manuellement pour cet employé"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={control}
              name="employeeId"
              render={({ field }) => {
                // Trouver l'employé sélectionné pour afficher son nom
                const selectedEmployee = users.find(
                  (user) => user.id === field.value
                );
                const currentEmployeeName = selectedEmployee
                  ? selectedEmployee.firstName && selectedEmployee.lastName
                    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                    : selectedEmployee.firstName ||
                      selectedEmployee.lastName ||
                      "Employé"
                  : "Sélectionner un employé";

                if (employeeId || isEdit) {
                  return (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base">
                        Employé <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          value={currentEmployeeName}
                          disabled
                          className="bg-muted cursor-not-allowed"
                          readOnly
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }

                return (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Employé <span className="text-red-500">*</span>
                    </FormLabel>
                    <Popover
                      open={employeePopoverOpen}
                      onOpenChange={(nextOpen) => {
                        setEmployeePopoverOpen(nextOpen);
                        if (!nextOpen) {
                          setEmployeeSearch("");
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "w-full justify-between",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {currentEmployeeName}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandInput
                            placeholder="Rechercher un employé..."
                            value={employeeSearch}
                            onValueChange={(value) => {
                              setEmployeeSearch(value);
                            }}
                          />
                          <CommandList>
                            {isFetchingUsers ? (
                              <div className="py-3 text-center text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </div>
                            ) : users.length === 0 ? (
                              <CommandEmpty>Aucun employé trouvé.</CommandEmpty>
                            ) : (
                              users
                                .filter((user) => user.role === "EMPLOYEE")
                                .map((user) => (
                                  <CommandItem
                                    key={user.id}
                                    value={
                                      user.firstName && user.lastName
                                        ? `${user.firstName} ${user.lastName}`
                                        : ""
                                    }
                                    onSelect={() => {
                                      field.onChange(user.id);
                                      setEmployeePopoverOpen(false);
                                      setEmployeeSearch("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        user.id === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    {user.firstName && user.lastName
                                      ? `${user.firstName} ${user.lastName}`
                                      : ""}
                                    {user.company && ` - ${user.company.name}`}
                                  </CommandItem>
                                ))
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Date et heure de début */}
            <div className="space-y-2">
              <FormLabel className="text-sm sm:text-base">
                Date et heure de début <span className="text-red-500">*</span>
              </FormLabel>
              <div className="flex flex-col sm:flex-row gap-2">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <Popover
                        open={startDatePopoverOpen}
                        onOpenChange={setStartDatePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                              <span className="truncate">
                                {field.value ? (
                                  format(field.value, "PPP", { locale: fr })
                                ) : (
                                  <span>Sélectionner une date</span>
                                )}
                              </span>
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setStartDatePopoverOpen(false);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem className="w-full sm:w-[120px]">
                      <FormControl>
                        <Input type="time" {...field} className="w-full" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Date et heure de fin (optionnel) */}
            <div className="space-y-2">
              <FormLabel className="text-sm sm:text-base">Date et heure de fin (optionnel)</FormLabel>
              <div className="flex flex-col sm:flex-row gap-2">
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <Popover
                        open={endDatePopoverOpen}
                        onOpenChange={setEndDatePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                              <span className="truncate">
                                {field.value ? (
                                  format(field.value, "PPP", { locale: fr })
                                ) : (
                                  <span>Sélectionner une date</span>
                                )}
                              </span>
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={field.value || undefined}
                            onSelect={(date) => {
                              field.onChange(date || null);
                              setEndDatePopoverOpen(false);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <FormItem className="w-full sm:w-[120px]">
                      <FormControl>
                        <Input
                          type="time"
                          {...field}
                          value={field.value || ""}
                          disabled={!endDate}
                          onChange={(e) => {
                            field.onChange(e.target.value || "");
                          }}
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Message d'aide ou d'erreur */}
              {endDate && !endTime && (
                <p className="text-xs text-destructive">
                  Veuillez sélectionner une heure de fin
                </p>
              )}
              {!endDate && (
                <p className="text-xs text-muted-foreground">
                  Laissez vide pour une session en cours
                </p>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="submit"
                disabled={isPending || !isFormValid}
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? "Enregistrement..." : "Création..."}
                  </>
                ) : isEdit ? (
                  markAsReviewedOnSave ? "Enregistrer et marquer traité" : "Enregistrer"
                ) : (
                  "Créer"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default WorSessionFormDialog;
